/**
 * GraphManager - 正規化されたグラフ構造の管理（ファサード）
 * SessionManagerの後継として、各サービスを統括
 */

import type { 
  AppState, 
  Image, 
  ImageSet, 
  SetItem, 
  Node, 
  Connection,
  SetViewModel,
  GraphViewModel,
  GraphOperation
} from './types'
import { ImageStore } from './ImageStore'
import { SetService } from './services/SetService'
import { ItemService } from './services/ItemService'
import { NodeService } from './services/NodeService'
import { QueryService } from './services/QueryService'
import { CRDTService } from './services/CRDTService'
import { PersistenceService } from './services/PersistenceService'

export class GraphManager {
  private state: AppState
  private imageStore: ImageStore
  private itemService: ItemService
  private crdtService: CRDTService
  private persistenceService: PersistenceService
  private listeners: Set<(state: AppState) => void> = new Set()
  private autoSave: () => void

  private initialized = false

  constructor() {
    // サービスの初期化
    this.imageStore = new ImageStore()
    this.itemService = new ItemService(this.imageStore)
    this.crdtService = new CRDTService()
    this.persistenceService = new PersistenceService()
    
    // 初期状態
    this.state = this.createEmptyState()
    
    // 自動保存設定（ただし初期化まで発動しない）
    this.autoSave = () => {}
  }

  /**
   * 初期化メソッド - 一度だけ実行
   */
  public init(): void {
    if (this.initialized) return
    this.initialized = true
    
    // 復元処理
    this.restore()
    
    // CRDT同期設定
    this.setupCRDTSync()
    
    // 自動保存設定
    this.autoSave = this.persistenceService.setupAutoSave(() => {
      this.persist()
    })
  }

  /**
   * 空の初期状態を作成
   */
  private createEmptyState(): AppState {
    return {
      images: {},
      imageSets: {},
      setItems: {},
      nodes: {},
      connections: {},
      meta: {
        version: '2.0.0',
        lastModified: new Date()
      }
    }
  }

  // ============================================
  // 状態管理
  // ============================================

  /**
   * 現在の状態を取得
   */
  getState(): AppState {
    return this.state
  }

  /**
   * 状態変更をリスナーに通知
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.state))
  }

  /**
   * 状態変更リスナーを登録
   */
  subscribe(listener: (state: AppState) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * 操作を適用
   */
  private applyOperation(operation: GraphOperation): void {
    switch (operation.type) {
      case 'ADD_IMAGE':
        this.state.images[operation.payload.id] = operation.payload
        break
      case 'ADD_SET':
        this.state.imageSets[operation.payload.id] = operation.payload
        break
      case 'ADD_ITEM':
        this.state.setItems[operation.payload.id] = operation.payload
        break
      case 'ADD_NODE':
        this.state.nodes[operation.payload.id] = operation.payload
        break
      case 'ADD_CONNECTION':
        this.state.connections[operation.payload.id] = operation.payload
        break
      case 'UPDATE_ITEM_ORDER': {
        const item = this.state.setItems[operation.payload.itemId]
        if (item) {
          item.order = operation.payload.newOrder
        }
        break
      }
      case 'DELETE_IMAGE':
        delete this.state.images[operation.payload.imageId]
        break
      case 'DELETE_SET':
        delete this.state.imageSets[operation.payload.setId]
        break
      case 'DELETE_ITEM':
        delete this.state.setItems[operation.payload.itemId]
        break
      case 'DELETE_NODE':
        delete this.state.nodes[operation.payload.nodeId]
        break
      case 'DELETE_CONNECTION':
        delete this.state.connections[operation.payload.connectionId]
        break
    }
    
    this.state.meta.lastModified = new Date()
    this.crdtService.syncToCRDT(this.state)
    this.autoSave()
    this.notifyListeners()
  }

  /**
   * 複数の操作を適用
   */
  private applyOperations(operations: GraphOperation[]): void {
    operations.forEach(op => this.applyOperation(op))
  }

  // ============================================
  // ImageSet操作
  // ============================================

  /**
   * 新しいImageSetを作成
   */
  createSet(name?: string, type: 'linear' | 'circular' = 'linear'): ImageSet {
    const set = SetService.createSet(name, type)
    this.applyOperation({ type: 'ADD_SET', payload: set })
    return set
  }

  /**
   * ImageSetを削除（関連するアイテムとノードも削除）
   */
  deleteSet(setId: string): void {
    const operations = Array.from(SetService.deleteSet(this.state, setId))
    this.applyOperations(operations)
  }

  // ============================================
  // Image & SetItem操作
  // ============================================

  /**
   * 画像を追加してSetItemを作成
   */
  async addImageToSet(
    setId: string, 
    imageData: string, 
    position?: number
  ): Promise<{ image: Image; item: SetItem }> {
    const result = await this.itemService.addImageToSet(this.state, setId, imageData, position)
    this.applyOperations(result.operations)
    return { image: result.image, item: result.item }
  }

  /**
   * SetItemの順序を変更
   */
  moveItem(itemId: string, newPosition: number): void {
    const operation = ItemService.moveItem(this.state, itemId, newPosition)
    if (operation) {
      this.applyOperation(operation)
    }
  }

  /**
   * 画像とSetItemを削除
   */
  async deleteImage(imageId: string): Promise<void> {
    const operations: GraphOperation[] = []
    for await (const op of this.itemService.deleteImage(this.state, imageId)) {
      operations.push(op)
    }
    this.applyOperations(operations)
  }

  // ============================================
  // Node & Connection操作
  // ============================================

  /**
   * ノードを作成
   */
  createNode(
    setId: string,
    imageId: string,
    label?: string,
    position?: 'first' | 'last' | 'middle'
  ): Node {
    const node = NodeService.createNode(setId, imageId, label, position)
    this.applyOperation({ type: 'ADD_NODE', payload: node })
    return node
  }

  /**
   * 2つのノード間に接続を作成
   */
  createConnection(
    fromNodeId: string,
    toNodeId: string,
    type?: 'transition' | 'branch' | 'merge',
    bidirectional = false
  ): Connection {
    const connection = NodeService.createConnection(fromNodeId, toNodeId, type, bidirectional)
    this.applyOperation({ type: 'ADD_CONNECTION', payload: connection })
    return connection
  }

  /**
   * ノードを削除
   */
  deleteNode(nodeId: string): void {
    this.applyOperation({ type: 'DELETE_NODE', payload: { nodeId } })
  }

  /**
   * 接続を削除
   */
  deleteConnection(connectionId: string): void {
    this.applyOperation({ type: 'DELETE_CONNECTION', payload: { connectionId } })
  }

  /**
   * 2つのセット間を接続（簡易メソッド）
   */
  connectSets(
    fromSetId: string,
    fromImageId: string,
    toSetId: string,
    toImageId: string,
    bidirectional = false
  ): void {
    const operations = Array.from(
      NodeService.connectSets(this.state, fromSetId, fromImageId, toSetId, toImageId, bidirectional)
    )
    this.applyOperations(operations)
  }

  // ============================================
  // クエリメソッド
  // ============================================

  /**
   * SetIDから順序通りのSetItemsを取得
   */
  getSetItems(setId: string): SetItem[] {
    return SetService.getSetItems(this.state, setId)
  }

  /**
   * SetViewModelを生成
   */
  getSetViewModel(setId: string): SetViewModel | null {
    return QueryService.getSetViewModel(this.state, setId)
  }

  /**
   * GraphViewModelを生成
   */
  getGraphViewModel(): GraphViewModel {
    return QueryService.getGraphViewModel(this.state)
  }

  /**
   * 画像を取得（Base64形式）
   */
  async getImage(imageId: string): Promise<string | null> {
    return this.itemService.getImage(imageId)
  }

  /**
   * サムネイルを取得
   */
  async getThumbnail(imageId: string): Promise<string | null> {
    return this.itemService.getThumbnail(imageId)
  }

  // ============================================
  // ストレージ情報
  // ============================================

  /**
   * ストレージ使用状況を取得
   */
  async getStorageInfo(): Promise<{
    imageCount: number
    setCount: number
    itemCount: number
    nodeCount: number
    connectionCount: number
    storageUsed: number
  }> {
    const storageInfo = await this.imageStore.getStorageInfo()
    return Promise.resolve(QueryService.getStorageInfo(this.state, storageInfo.used))
  }

  /**
   * 全データをクリア
   */
  async clearAll(): Promise<void> {
    await this.imageStore.clear()
    this.state = this.createEmptyState()
    this.crdtService.clear()
    this.persistenceService.clear()
    this.notifyListeners()
  }

  // ============================================
  // CRDT同期
  // ============================================

  /**
   * CRDTからの同期
   */
  private syncFromCRDT(): void {
    const crdtState = this.crdtService.syncFromCRDT()
    if (crdtState) {
      this.state = crdtState
      this.notifyListeners()
    }
  }

  /**
   * CRDT同期の設定
   */
  private setupCRDTSync(): void {
    this.crdtService.observeChanges(() => {
      this.syncFromCRDT()
    })
  }

  // ============================================
  // 永続化
  // ============================================

  /**
   * localStorageに永続化
   */
  private persist(): void {
    const crdtState = Array.from(this.crdtService.encodeState())
    this.persistenceService.persist(this.state, crdtState)
  }

  /**
   * localStorageから復元
   */
  private restore(): void {
    const restored = this.persistenceService.restore()
    if (!restored) return
    
    const { appState, ydocState } = restored
    this.state = appState
    
    // Yjsの状態も復元
    if (ydocState) {
      this.crdtService.applyUpdate(new Uint8Array(ydocState))
    }
    
    this.notifyListeners()
  }
}

// FractionalIndexもエクスポート（後方互換性のため）
export { FractionalIndex } from './utils/FractionalIndex'